/**
 * Connected admin releases with data fetching and mutations.
 *
 * Self-contained - no props needed.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { createReleasesApi } from "@animus/frontend-features/admin/releases";
import {
  useApiConfig,
  useAuthStrategy,
} from "@animus/frontend-features/config";

import type { FreezeStatusMap, Release, ReleasePlatform } from "./types.ts";
import { AdminReleases } from "./admin-releases.tsx";

type FilterPlatform = ReleasePlatform | "all";

function AdminReleasesConnected() {
  const { baseUrl } = useApiConfig();
  const auth = useAuthStrategy();
  const api = useMemo(() => createReleasesApi(baseUrl, auth), [baseUrl, auth]);

  const [filter, setFilter] = useState<FilterPlatform>("all");
  const [releases, setReleases] = useState<Release[]>([]);
  const [freezeStatus, setFreezeStatus] = useState<FreezeStatusMap | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPinning, setIsPinning] = useState(false);
  const [isUnpinning, setIsUnpinning] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const [releasesResult, freezeResult] = await Promise.all([
      api.listReleases({
        ...(filter !== "all" && { platform: filter }),
        limit: 50,
      }),
      api.getFreezeStatus(),
    ]);

    if (releasesResult.isErr) {
      setError(releasesResult.error.message ?? "Failed to fetch releases");
    } else {
      setReleases(
        releasesResult.value.releases.map((r) => ({
          id: r.id,
          platform: r.platform,
          gitSha: r.gitSha,
          commitMessage: r.commitMessage,
          author: r.author,
          timestamp: r.timestamp,
          githubUrl: r.githubUrl,
          isActive: r.isActive,
          ...(r.isCompatible !== undefined && { isCompatible: r.isCompatible }),
          ...(r.compatibilityWarning !== undefined && {
            compatibilityWarning: r.compatibilityWarning,
          }),
        }))
      );
    }

    if (freezeResult.isOk && freezeResult.value.freezes) {
      setFreezeStatus({
        web: freezeResult.value.freezes.web ?? {
          isFrozen: false,
          pinnedVersion: null,
          reason: null,
        },
        marketing: freezeResult.value.freezes.marketing ?? {
          isFrozen: false,
          pinnedVersion: null,
          reason: null,
        },
        mobile: freezeResult.value.freezes.mobile ?? {
          isFrozen: false,
          pinnedVersion: null,
          reason: null,
        },
      });
    }

    setIsLoading(false);
  }, [api, filter]);

  useEffect(() => {
    fetchData().catch(() => {
      // Error already handled in fetchData
    });
  }, [fetchData]);

  async function handlePin(
    platform: ReleasePlatform,
    gitSha: string,
    reason?: string
  ) {
    setIsPinning(true);
    const result = await api.freezeApp(platform, gitSha, reason);
    setIsPinning(false);

    if (result.isErr) {
      setError(result.error.message ?? "Failed to freeze app");
      return;
    }

    await fetchData();
  }

  async function handleUnpin(platform: ReleasePlatform) {
    setIsUnpinning(true);
    const result = await api.unfreezeApp(platform);
    setIsUnpinning(false);

    if (result.isErr) {
      setError(result.error.message ?? "Failed to unfreeze app");
      return;
    }

    await fetchData();
  }

  function handleFilterChange(newFilter: FilterPlatform) {
    setFilter(newFilter);
  }

  return (
    <AdminReleases
      error={error}
      freezeStatus={freezeStatus}
      isLoading={isLoading}
      isPinning={isPinning}
      isUnpinning={isUnpinning}
      onFilterChange={handleFilterChange}
      onPin={handlePin}
      onRefresh={fetchData}
      onUnpin={handleUnpin}
      releases={releases}
    />
  );
}

export { AdminReleasesConnected };
